import React, { useState, useEffect, useMemo, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaSearch, FaPrint, FaDownload, FaChartBar, FaTimesCircle, FaTimes } from "react-icons/fa";
import api from "../../services/api";
import "./admin.css";
import { useSearchParams } from "react-router-dom";

const AttendanceReport = () => {
  const [employees, setEmployees] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [roleFilter, setRoleFilter] = useState("all"); // ✅ NEW: Role filter from URL
  const [employeeNameFromURL, setEmployeeNameFromURL] = useState(""); 
  const [isUrlParamsLoaded, setIsUrlParamsLoaded] = useState(false); 

  const printRef = useRef();
  const [searchParams] = useSearchParams();

  const getToken = () => localStorage.getItem("token");

  // ✅ Handle URL parameters
  useEffect(() => {
    const employeeId = searchParams.get('employee');
    const employeeName = searchParams.get('employeeName');
    const startDateParam = searchParams.get('start');
    const endDateParam = searchParams.get('end');
    const roleParam = searchParams.get('role'); // ✅ Read role from URL

    if (employeeId) {
      setSelectedEmployee(employeeId);
    }
    if (employeeName) {
      setEmployeeNameFromURL(decodeURIComponent(employeeName));
    }
    if (startDateParam) {
      setStartDate(startDateParam);
    }
    if (endDateParam) {
      setEndDate(endDateParam);
    }
    // ✅ Set role from URL
    if (roleParam && ['employee', 'intern', 'student'].includes(roleParam)) {
      setRoleFilter(roleParam);
    } else {
      setRoleFilter('all');
    }

    setIsUrlParamsLoaded(true);
  }, [searchParams]);

  const handleGetEmployees = async () => {
    try {
      const token = getToken();
      const response = await api.get("/admin/get_employees", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        const employeesData = response.data.employees || [];
        const processedEmployees = employeesData.map(emp => ({
          ...emp,
          status: emp.status !== undefined && emp.status !== null ? parseInt(emp.status) : 1
        }));
        setEmployees(processedEmployees);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleGetAllAttendance = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      const response = await api.get("/admin/get_all_attendance", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setAttendances(response.data.attendances || []);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Failed to fetch attendance");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterAttendance = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      const params = {};
      if (selectedEmployee !== "all") params.employee_id = selectedEmployee;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get("/admin/filter_attendance", {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setAttendances(response.data.attendances || []);
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Filter failed");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return { date: "—", time: "—", fullDate: "—" };
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return { date: "—", time: "—", fullDate: "—" };
    return {
      date: date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }),
      time: date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
      fullDate: date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }),
      isoDate: date.toISOString().split('T')[0],
      isoTime: date.toTimeString().split(' ')[0],
    };
  };

  const formatTime12Hour = (timeString) => {
    if (!timeString) return "—";
    if (timeString.includes("T") || timeString.includes("-")) {
      return formatDateTime(timeString).time;
    }
    const parts = timeString.split(":");
    if (parts.length < 2) return timeString;
    let hour = parseInt(parts[0], 10);
    const minute = parts[1];
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    hour = hour ? hour : 12;
    return `${hour.toString().padStart(2, '0')}:${minute} ${ampm}`;
  };

  const getWorkingHours = (checkIn, checkOut, totalPauseMinutes = 0) => {
    if (!checkIn || !checkOut) return "—";
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const totalMinutes = Math.floor((end - start) / (1000 * 60));
    const actualMinutes = totalMinutes - (totalPauseMinutes || 0);
    if (actualMinutes <= 0) return "0m";
    const hours = Math.floor(actualMinutes / 60);
    const minutes = actualMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  const getWorkingHoursDecimal = (checkIn, checkOut, totalPauseMinutes = 0) => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const totalMinutes = Math.floor((end - start) / (1000 * 60));
    const actualMinutes = totalMinutes - (totalPauseMinutes || 0);
    return (actualMinutes / 60).toFixed(2);
  };

  const getArrivalDeviation = (checkInStr, expectedArrivalStr) => {
    if (!checkInStr || !expectedArrivalStr) return null;
    const checkIn = new Date(checkInStr);
    const expected = new Date(checkInStr);
    const [hours, minutes] = expectedArrivalStr.split(":").map(Number);
    expected.setHours(hours, minutes, 0, 0);
    const diffMs = checkIn - expected;
    if (diffMs === 0) return null;
    const absMinutes = Math.abs(Math.floor(diffMs / 60000));
    return diffMs > 0
      ? { minutes: absMinutes, type: 'late' }
      : { minutes: absMinutes, type: 'early' };
  };

  const getDepartureDeviation = (checkOutStr, expectedLeavingStr) => {
    if (!checkOutStr || !expectedLeavingStr) return null;
    const checkOut = new Date(checkOutStr);
    const expected = new Date(checkOutStr);
    const [hours, minutes] = expectedLeavingStr.split(":").map(Number);
    expected.setHours(hours, minutes, 0, 0);
    const diffMs = checkOut - expected;
    if (diffMs === 0) return null;
    const absMinutes = Math.abs(Math.floor(diffMs / 60000));
    return diffMs > 0
      ? { minutes: absMinutes, type: 'extra' }
      : { minutes: absMinutes, type: 'early' };
  };

  const formatDuration = (totalMinutes) => {
    if (totalMinutes === null || totalMinutes === undefined) return '';
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const employeeRoleMap = useMemo(() => {
    const map = {};
    employees.forEach(emp => {
      map[emp.employeeName] = emp.role;
    });
    return map;
  }, [employees]);

  const employeeStatusMap = useMemo(() => {
    const map = {};
    employees.forEach(emp => {
      map[emp.employeeName] = emp.status !== undefined ? emp.status : 1;
    });
    return map;
  }, [employees]);

  // ✅ Employees dropdown - role aur status ke hisaab se filtered
  const employeesFiltered = useMemo(() => {
    let filtered = employees;
    
    // Role filter
    if (roleFilter !== "all") {
      filtered = filtered.filter(emp => emp.role === roleFilter);
    }
    
    // Status filter
    if (statusFilter === "active") {
      filtered = filtered.filter(emp => (emp.status !== undefined ? emp.status : 1) === 1);
    } else if (statusFilter === "deactive") {
      filtered = filtered.filter(emp => (emp.status !== undefined ? emp.status : 1) === 0);
    }
    
    return filtered;
  }, [employees, roleFilter, statusFilter]);

  useEffect(() => {
    setSelectedEmployee("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, roleFilter]);

  // ✅ Filter attendances with search, role, and status
  const filteredAttendances = attendances.filter((att) => {
    const matchesSearch =
      att.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      att.employee_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      att.employee_fields?.toLowerCase().includes(searchTerm.toLowerCase());

    const attRole = employeeRoleMap[att.employee_name] || 'employee';
    const matchesRole = roleFilter === "all" || attRole === roleFilter;

    const empStatus = employeeStatusMap[att.employee_name] !== undefined ? employeeStatusMap[att.employee_name] : 1;
    const matchesStatus = statusFilter === "all" ||
      (statusFilter === "active" && empStatus === 1) ||
      (statusFilter === "deactive" && empStatus === 0);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const isFiltered = searchTerm || selectedEmployee !== "all" || startDate || endDate;
  const isDateFilterActive = startDate || endDate;

  const sortedFilteredAttendances = useMemo(() => {
    if (!isFiltered) return [];
    return [...filteredAttendances].sort((a, b) => {
      if (!a.check_in || !b.check_in) return 0;
      const diff = new Date(a.check_in) - new Date(b.check_in);
      return isDateFilterActive ? diff : -diff;
    });
  }, [filteredAttendances, isFiltered, isDateFilterActive]);

  const groupedAttendances = useMemo(() => {
    if (isFiltered) return [];
    const groups = {};
    filteredAttendances.forEach((attendance) => {
      let dateKey = "unknown";
      let displayDate = "Unknown Date";
      if (attendance.check_in) {
        const d = new Date(attendance.check_in);
        if (!isNaN(d.getTime())) {
          dateKey = d.toISOString().split("T")[0];
          displayDate = formatDateTime(attendance.check_in).fullDate;
        }
      }
      if (!groups[dateKey]) {
        groups[dateKey] = { date: dateKey, displayDate, records: [] };
      }
      groups[dateKey].records.push(attendance);
    });
    return Object.values(groups).sort((a, b) => {
      if (a.date === "unknown") return 1;
      if (b.date === "unknown") return -1;
      return new Date(b.date) - new Date(a.date);
    });
  }, [filteredAttendances, isFiltered]);

  useEffect(() => {
    handleGetEmployees();
  }, []);

  useEffect(() => {
    if (employees.length > 0 && isUrlParamsLoaded) {
      if (selectedEmployee !== "all" || startDate || endDate) {
        handleFilterAttendance();
      } else {
        handleGetAllAttendance();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees.length, isUrlParamsLoaded, selectedEmployee, startDate, endDate]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadCSV = () => {
    if (filteredAttendances.length === 0) {
      setErrorMsg("No records to download");
      setTimeout(() => setErrorMsg(""), 3000);
      return;
    }

    const headers = [
      'Employee Name', 'Employee Email', 'Field', 'Date',
      'Check In', 'Check Out', 'Working Hours', 'Break Minutes', 'Status',
      'Expected Arrival', 'Expected Leaving', 'Late/Early Arrival', 'Early/Extra Departure'
    ];

    const rows = filteredAttendances.map((att) => {
      const dateInfo = formatDateTime(att.check_in);
      const arrivalDev = getArrivalDeviation(att.check_in, att.expected_arrival);
      const departureDev = getDepartureDeviation(att.check_out, att.expected_leaving);
      const pauseMinutes = att.total_pause_minutes || 0;

      let arrivalNote = '';
      if (arrivalDev) {
        arrivalNote = arrivalDev.type === 'late' ? `Late ${arrivalDev.minutes}m` : `Early ${arrivalDev.minutes}m`;
      }

      let departureNote = '';
      if (departureDev) {
        departureNote = departureDev.type === 'extra' ? `Extra ${departureDev.minutes}m` : `Early ${departureDev.minutes}m`;
      }

      return [
        `"${att.employee_name || ''}"`,
        `"${att.employee_email || ''}"`,
        `"${att.employee_fields || ''}"`,
        dateInfo.isoDate || '',
        att.check_in ? formatDateTime(att.check_in).isoTime : '',
        att.check_out ? formatDateTime(att.check_out).isoTime : '',
        getWorkingHoursDecimal(att.check_in, att.check_out, pauseMinutes),
        pauseMinutes,
        att.status || 'present',
        att.expected_arrival || '',
        att.expected_leaving || '',
        `"${arrivalNote}"`,
        `"${departureNote}"`
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const timeStr = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}`;
    const filename = `attendance_report_${dateStr}_${timeStr}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderRecordRow = (attendance) => {
    const checkIn = formatDateTime(attendance.check_in);
    const checkOut = formatDateTime(attendance.check_out);
    const arrivalDev = getArrivalDeviation(attendance.check_in, attendance.expected_arrival);
    const departureDev = getDepartureDeviation(attendance.check_out, attendance.expected_leaving);
    const pauseMinutes = attendance.total_pause_minutes || 0;

    return (
      <tr key={attendance.id}>
        <td>
          <div className="student-info">
            <div className="student-avatar">{attendance.employee_name?.charAt(0) || 'E'}</div>
            <span className="employee-name-text">{attendance.employee_name}</span>
          </div>
        </td>
        <td><span className="field-badge">{attendance.employee_fields || '—'}</span></td>
        <td><span className="date-badge">{checkIn.date}</span></td>
        <td>
          <div className="time-cell-stack">
            <span className="time-badge time-in">{checkIn.time}</span>
            {arrivalDev && arrivalDev.type === 'early' && (
              <span className="status-badge status-early">Early {formatDuration(arrivalDev.minutes)}</span>
            )}
            {arrivalDev && arrivalDev.type === 'late' && (
              <span className="status-badge status-late">Late {formatDuration(arrivalDev.minutes)}</span>
            )}
          </div>
        </td>
        <td>
          <div className="time-cell-stack">
            <span className="time-badge time-out">{checkOut.time}</span>
            {departureDev && departureDev.type === 'early' && (
              <span className="status-badge status-absent">Early {formatDuration(departureDev.minutes)}</span>
            )}
            {departureDev && departureDev.type === 'extra' && (
              <span className="status-badge status-extra">Extra {formatDuration(departureDev.minutes)}</span>
            )}
          </div>
        </td>
        <td>
          <span className="hours-badge">{getWorkingHours(attendance.check_in, attendance.check_out, pauseMinutes)}</span>
          {pauseMinutes > 0 && (
            <span style={{ fontSize: '10px', color: '#f59e0b', display: 'block', marginTop: '3px', fontWeight: '500' }}>
              -{formatDuration(pauseMinutes)} Outside Office
            </span>
          )}
        </td>
        <td>
          <span className={`status-badge status-${attendance.status?.toLowerCase() || 'present'}`}>
            {attendance.status || 'Present'}
          </span>
        </td>
        <td><span className="time-badge time-in">{formatTime12Hour(attendance.expected_arrival)}</span></td>
        <td><span className="time-badge time-out">{formatTime12Hour(attendance.expected_leaving)}</span></td>
      </tr>
    );
  };

  const getFilteredEmployeeName = () => {
    if (employeeNameFromURL) return employeeNameFromURL;
    if (selectedEmployee === "all") return null;
    const emp = employees.find(e => e.id.toString() === selectedEmployee.toString());
    return emp ? emp.employeeName || emp.name : null;
  };

  const filteredEmployeeName = getFilteredEmployeeName();

  const getRoleLabel = () => {
    if (roleFilter === 'employee') return 'Employees';
    if (roleFilter === 'intern') return 'Interns';
    if (roleFilter === 'student') return 'Students';
    return 'All Members';
  };

  return (
    <div className="admin-container">
      <div className="header-banner mb-4 no-print">
        <div className="header-content">
          <div>
            <h1 className="header-title">
              {getRoleLabel()} Attendance
            </h1>
            <p className="header-subtitle">
              {statusFilter === "deactive"
                ? `Viewing ex-members (${getRoleLabel()}) attendance records`
                : filteredEmployeeName
                ? `Viewing ${filteredEmployeeName}'s attendance records`
                : `View all ${getRoleLabel()} attendance records grouped by date`
              }
            </p>
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="error-message no-print">
          <FaTimesCircle style={{ marginRight: "6px" }} /> {errorMsg}
        </div>
      )}

      <div className="students-card">
        <div className="card-header-custom no-print">
          <div>
            <h3 className="card-title">
              {getRoleLabel()} Attendance Records
            </h3>
            <p className="card-subtitle">
              {filteredAttendances.length} records across {groupedAttendances.length} days
            </p>
          </div>
          <div className="header-actions">
            <div className="search-box">
              <span className="search-icon"><FaSearch /></span>
              <input
                type="text"
                placeholder="Search by name, email or field..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* ✅ Status Filter Dropdown */}
            <select
              className="form-input filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="active">Active Members</option>
              <option value="deactive">Ex-Members</option>
            </select>

            {/* ✅ Employee dropdown - role aur status ke hisaab se */}
            <select
              className="form-input filter-select"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value="all">
                All {statusFilter === "deactive" ? "Ex-Members" : getRoleLabel()}
              </option>
              {employeesFiltered.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.employeeName || emp.name}
                </option>
              ))}
            </select>

            <div className="date-range">
              <input
                type="date"
                className="form-input filter-select"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start date"
              />
              <span className="date-separator-text">to</span>
              <input
                type="date"
                className="form-input filter-select"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End date"
              />
            </div>
            <button className="btn-add-student no-print" onClick={handlePrint}>
              <FaPrint style={{ marginRight: "6px" }} /> Print
            </button>
            <button className="btn-add-student no-print" onClick={handleDownloadCSV}>
              <FaDownload style={{ marginRight: "6px" }} /> CSV
            </button>
          </div>
        </div>

        <div className="table-wrapper" ref={printRef}>
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "60px" }}>
              <div className="loading-spinner no-print"></div>
              <p style={{ marginTop: "15px", color: "#888" }} className="no-print">Loading records...</p>
            </div>
          ) : filteredAttendances.length > 0 ? (
            isFiltered ? (
              <table className="students-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Field</th>
                    <th>Date</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Expected Arrival</th>
                    <th>Expected Leaving</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFilteredAttendances.map(renderRecordRow)}
                </tbody>
              </table>
            ) : (
              <div className="grouped-records">
                {groupedAttendances.map((group) => (
                  <div key={group.date} className="date-group">
                    <table className="students-table date-group-table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Field</th>
                          <th>Date</th>
                          <th>Check In</th>
                          <th>Check Out</th>
                          <th>Hours</th>
                          <th>Status</th>
                          <th>Expected Arrival</th>
                          <th>Expected Leaving</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.records.map(renderRecordRow)}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="empty-state py-4 no-print">
              <div className="empty-state-content">
                <div className="empty-icon"><FaChartBar /></div>
                <p className="empty-text">No attendance records found</p>
                <p className="empty-subtext">
                  {statusFilter === "deactive"
                    ? `No attendance records found for ex-members (${getRoleLabel()}).`
                    : `No records found for ${getRoleLabel()} in the selected date range.`
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;